import React, { FC, useState, useEffect } from 'react';
import { snakeToCamel } from '../../caseUtils';
import {
  CustomWindow,
  LawyerContextType,
  LawyerExperiences,
} from './lawyer.types';
import { fetchLawyerDecisions } from '../lawyer.api';
import { flattenDomains } from '../../entity/expertises/entityExpertises.utils';
import { buildUrlWithParameters } from '../../../admin/benchmarks/urlUtils';
import { getPageName } from '../../utils/pageName';

declare let window: CustomWindow;

export const LawyerContext = React.createContext<LawyerContextType>({
  experience: { decisionCount: 0, domains: [], juridictions: [], villes: [] },
  lawyerInfos: window.lawyer_info,
  lawyerHasRetired: false,
  lawyerIsCurrentUser: false,
  lawyerIsVerified: false,
  lawyerImg: '',
  lawyerName: '',
  allDecisionsLink: '',
});

export const LawyerProvider: FC = ({ children }) => {
  const lawyerInfos = snakeToCamel(window.lawyer_info);
  const [experience, setExperience] = useState<LawyerExperiences>({
    decisionCount: 0,
    domains: [],
    juridictions: [],
    villes: [],
  });

  useEffect(() => {
    async function load() {
      const {
        domains,
        juridictions,
        villes,
        decisionCount,
      } = await fetchLawyerDecisions(lawyerInfos.lawyerId);
      setExperience({
        domains: flattenDomains(domains),
        juridictions,
        villes,
        decisionCount,
      });
    }
    load();
  }, [lawyerInfos.lawyerId]);

  const lawyerName = lawyerInfos
    ? `${lawyerInfos.firstname} ${lawyerInfos.lastname}`
    : '';

  const allDecisionsLink = buildUrlWithParameters('/search', {
    q: encodeURIComponent(lawyerName),
    chrono: true,
    lid: lawyerInfos.lawyerId,
    sourcePage: getPageName(),
  });

  return (
    <LawyerContext.Provider
      value={{
        experience,
        lawyerInfos,
        lawyerHasRetired: window.lawyer_has_retired,
        lawyerIsCurrentUser: window.lawyer_is_current_user,
        lawyerIsVerified: window.lawyer_is_verified,
        lawyerImg: window.lawyer_img,
        lawyerName,
        allDecisionsLink,
      }}
    >
      {children}
    </LawyerContext.Provider>
  );
};

export const withLawyer = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const ComponentWithLawyer: FC<Pick<
    P,
    Exclude<keyof P, keyof LawyerContextType>
  >> = props => (
    <LawyerContext.Consumer>
      {(lawyer: LawyerContextType) => (
        <Component {...(props as P)} {...lawyer} />
      )}
    </LawyerContext.Consumer>
  );
  return ComponentWithLawyer;
};
